import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Copy, Plus, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { buildFlightTextQuote } from '@/utils/flightTextQuote';
import { waLink } from '@/utils/phone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Flight text quote" — Additive to WhatsApp Path B. Generates a detailed
 * flight-itinerary WhatsApp message from itinerary.flightDetails (segments
 * only — fares/notes pasted alongside are never forwarded) + the ONE
 * lump-sum per-person fare computed from the selected pricing option, shown
 * in an editable box before sending. Same wa.me + mark-as-sent flow as the
 * existing "WhatsApp message (lump-sum)" action.
 */
const FlightTextQuoteDialog = ({ open, onOpenChange, quote, flightDetails, farePerPerson }) => {
  const [seatsLeft, setSeatsLeft] = useState('');
  const [fareRules, setFareRules] = useState(['']);
  const [text, setText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [hasSegments, setHasSegments] = useState(true);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSeatsLeft('');
    setFareRules(['']);
    setDirty(false);
    const built = buildFlightTextQuote({ flightDetails, farePerPerson, seatsLeft: '', fareRules: [] });
    setHasSegments(built.hasSegments);
    setWarnings(built.warnings || []);
    setText(built.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Live-regenerate while the agent is only touching the optional fields —
  // stop the moment they start editing the box itself directly.
  useEffect(() => {
    if (!open || dirty) return;
    const built = buildFlightTextQuote({ flightDetails, farePerPerson, seatsLeft, fareRules });
    setWarnings(built.warnings || []);
    setText(built.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatsLeft, fareRules]);

  const addFareRuleLine = () => setFareRules((prev) => [...prev, '']);
  const updateFareRuleLine = (i, val) => setFareRules((prev) => prev.map((r, idx) => (idx === i ? val : r)));
  const removeFareRuleLine = (i) => setFareRules((prev) => prev.filter((_, idx) => idx !== i));

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const openWhatsApp = async () => {
    window.open(waLink(quote.countryCode || '+91', quote.phone, text), '_blank', 'noopener,noreferrer');
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/mark-manual-sent`, {
        contextType: 'trip', contextId: quote.tripId, text,
      });
      toast.success('WhatsApp opened · logged in Chats as manually sent');
    } catch {
      toast.success('WhatsApp opened');
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="flight-text-quote-dialog">
        <DialogHeader>
          <DialogTitle>Flight text quote</DialogTitle>
          <DialogDescription>
            Segments come from Flight Details automatically. Fare is computed from the
            selected pricing option — never typed or pasted.
          </DialogDescription>
        </DialogHeader>

        {!hasSegments ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="flight-text-no-segments">
            No flight segments found in Flight Details. Add the GDS/PNR lines on the
            itinerary's Flight Details field first, then reopen this.
          </div>
        ) : (
          <div className="space-y-4">
            {warnings.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 space-y-1" data-testid="flight-text-duration-warning">
                {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
            <div>
              <Label>Seats left (optional)</Label>
              <Input
                data-testid="flight-text-seats-left-input"
                placeholder="e.g. 5"
                value={seatsLeft}
                onChange={(e) => setSeatsLeft(e.target.value)}
              />
            </div>
            <div>
              <Label>Fare rules (optional — add as many lines as needed)</Label>
              <div className="space-y-2">
                {fareRules.map((rule, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      data-testid={`flight-text-fare-rules-input-${i}`}
                      placeholder="e.g. Non Refundable / Non Changeable"
                      value={rule}
                      onChange={(e) => updateFareRuleLine(i, e.target.value)}
                    />
                    {fareRules.length > 1 && (
                      <Button
                        type="button" variant="ghost" size="sm"
                        onClick={() => removeFareRuleLine(i)}
                        data-testid={`flight-text-fare-rules-remove-${i}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addFareRuleLine} data-testid="flight-text-fare-rules-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add line
                </Button>
              </div>
            </div>
            <div>
              <Label>Message (edit freely before sending)</Label>
              <Textarea
                data-testid="flight-text-quote-textarea"
                rows={14}
                value={text}
                onChange={(e) => { setDirty(true); setText(e.target.value); }}
                className="font-mono text-xs"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="flight-text-cancel-btn">Cancel</Button>
          {hasSegments && (
            <>
              <Button variant="outline" onClick={copyText} data-testid="flight-text-copy-btn">
                <Copy className="h-4 w-4 mr-2" />Copy
              </Button>
              <Button onClick={openWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="flight-text-open-whatsapp-btn">
                <MessageCircle className="h-4 w-4 mr-2" />Open WhatsApp
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FlightTextQuoteDialog;
